import * as Sentry from '@sentry/capacitor';
import { App as CapApp } from '@capacitor/app';
import type { AppState } from '@capacitor/app';
import { logFriendlyObject } from '@otuvy/common';
import {
  isTransientDbSuspensionError,
  unwrapDexieError,
} from '../utils/state/model/implementations/dexie/dbSuspensionErrors';

/**
 * Diagnostic instrumentation for CC-1864 (Sentry issue OTUVY-FRONTLINE-5).
 *
 * When iOS suspends the app, WebKit aborts in-flight IndexedDB transactions. The app
 * syncs on a short interval, so backgrounding often lands mid-sync and the aborted
 * Dexie ops in fire-and-forget promise chains surface as anonymous unhandled rejections
 * with no stack trace ("Transaction aborted", "iterate a cursor that doesn't exist",
 * "without an in-progress transaction"). Because they carry no stack and no context,
 * the true cause was unprovable from the events alone.
 *
 * This module makes the cause observable:
 *  - stamps every Sentry event with the app lifecycle state (tag `app_state` + context)
 *  - records the current sync phase as breadcrumbs/context
 *  - captures the DB aborts from the known fire-and-forget paths WITH a call-site
 *    `origin` tag, a suspension-window flag, and a lifecycle snapshot, so "which call"
 *    and "was it around a background/resume transition" become queryable in Sentry.
 *
 * It is deliberately behavior-neutral: it observes only. It does not change the sync
 * sequence, does not suppress events, and does not touch liveQuery subscriptions. The
 * actual fix (awaiting cleanup, resume re-subscribe, benign-abort routing) is separate
 * and lands after this diagnostic release confirms the distribution.
 *
 * Because this code exists to catch failures, it must never fail the app itself: every
 * exported function traps its own errors, and the Capacitor plugin promises are caught.
 *
 * Note: on native platforms the Sentry SDK already emits an `app.lifecycle` breadcrumb
 * for state changes; ours is a deliberate JS-side sibling that adds timing data and
 * also exists on web.
 */

type LifecycleState = 'active' | 'background';

type LifecycleSnapshot = {
  state: LifecycleState;
  lastBackgroundedAt: string | null;
  lastResumedAt: string | null;
  msSinceBackground: number | null;
  msSinceResume: number | null;
};

// Module-scope lifecycle tracking. Stored as epoch ms; deltas are derived on read.
let currentState: LifecycleState = 'active';
let lastBackgroundedAtMs: number | null = null;
let lastResumedAtMs: number | null = null;
// True once a real appStateChange has been observed; blocks the async getState() seed
// from overwriting fresher listener data (the seed resolves after registration).
let transitionObserved = false;
let lifecycleTrackingInitialized = false;

export function getLifecycleSnapshot(): LifecycleSnapshot {
  return {
    state: currentState,
    lastBackgroundedAt: lastBackgroundedAtMs !== null ? new Date(lastBackgroundedAtMs).toISOString() : null,
    lastResumedAt: lastResumedAtMs !== null ? new Date(lastResumedAtMs).toISOString() : null,
    msSinceBackground: lastBackgroundedAtMs !== null ? Date.now() - lastBackgroundedAtMs : null,
    msSinceResume: lastResumedAtMs !== null ? Date.now() - lastResumedAtMs : null,
  };
}

/**
 * True when we are in, or near, a background/resume transition — the window where
 * WebKit suspension aborts surface.
 *
 * Covers BOTH halves of the suspension lifecycle: aborts that fire in the short grace
 * period after backgrounding (state is still 'background'), and aborts whose rejection
 * callbacks were frozen with the JS runtime and only run once the app RESUMES (state is
 * already 'active' again, and the backgrounding may be arbitrarily old — so recency of
 * the resume is what identifies them).
 */
export function isWithinSuspensionWindow(windowMs = 15_000): boolean {
  if (currentState === 'background') return true;
  const now = Date.now();
  if (lastBackgroundedAtMs !== null && now - lastBackgroundedAtMs < windowMs) return true;
  if (lastResumedAtMs !== null && now - lastResumedAtMs < windowMs) return true;
  return false;
}

function recordStateChange(isActive: boolean): void {
  transitionObserved = true;
  currentState = isActive ? 'active' : 'background';
  if (isActive) {
    lastResumedAtMs = Date.now();
  } else {
    lastBackgroundedAtMs = Date.now();
  }

  const snapshot = getLifecycleSnapshot();
  Sentry.setTag('app_state', currentState);

  // Only the STABLE fields go on the global scope. getLifecycleSnapshot()'s msSince*
  // values are deltas computed at call time, so storing them here would freeze them at
  // ~0 for as long as this context lives — every later unrelated event would then read
  // "the app resumed 0ms ago", which is worse than absent. The absolute timestamps are
  // stable, and a reader can derive the delta against the event timestamp.
  // captureDbAbort() passes a FRESH full snapshot per event, which is where the live
  // deltas belong and where CC-1864 actually needs them.
  Sentry.setContext('lifecycle', {
    state: snapshot.state,
    lastBackgroundedAt: snapshot.lastBackgroundedAt,
    lastResumedAt: snapshot.lastResumedAt,
  });

  // The breadcrumb DOES keep the full snapshot: a breadcrumb is a point-in-time record
  // stamped with its own timestamp, so deltas of ~0 are accurate there rather than stale.
  Sentry.addBreadcrumb({
    category: 'app.lifecycle',
    message: `app ${currentState}`,
    level: 'info',
    data: snapshot,
  });
}

/**
 * Register a single app-lifecycle listener that stamps every future Sentry event with
 * the current app state. Call once at bootstrap, after initSentry(). Idempotent.
 */
export function initLifecycleTracking(): void {
  if (lifecycleTrackingInitialized) return;
  lifecycleTrackingInitialized = true;

  try {
    Sentry.setTag('app_state', currentState);

    // Seed from the real state — an app can be launched directly into the background
    // (e.g. by a push), in which case the 'active' default would be wrong. Skipped if a
    // real transition has already been observed by the listener (the seed is staler).
    CapApp.getState()
      .then((state) => {
        if (transitionObserved) return;
        currentState = state.isActive ? 'active' : 'background';
        Sentry.setTag('app_state', currentState);
      })
      .catch((error) => console.warn('[sentryLogging] getState failed', logFriendlyObject(error)));

    CapApp.addListener('appStateChange', (state: AppState) => recordStateChange(state.isActive)).catch((error) =>
      console.warn('[sentryLogging] appStateChange listener registration failed', logFriendlyObject(error))
    );
  } catch (error) {
    console.warn('[sentryLogging] initLifecycleTracking failed', logFriendlyObject(error));
  }
}

/**
 * Record the current sync phase as a breadcrumb (always attached to the next captured
 * event) and on the `sync` context, so an abort event shows which phase was in flight.
 * The context carries a timestamp because it persists between syncs — `at` shows
 * whether the recorded phase is current or left over from a completed sync.
 */
export function logSyncPhase(phase: string, data?: Record<string, unknown>): void {
  try {
    Sentry.setContext('sync', { phase, at: new Date().toISOString(), ...data });
    Sentry.addBreadcrumb({
      category: 'sync',
      message: phase,
      level: 'info',
      data,
    });
  } catch (error) {
    console.warn('[sentryLogging] logSyncPhase failed', logFriendlyObject(error));
  }
}

/**
 * Capture a DB error from a known fire-and-forget sync path with the call-site `origin`,
 * whether it matched the transient-suspension classifier, and the current lifecycle
 * snapshot. Captures regardless of classification (diagnostic phase): the goal is to see
 * the true distribution — which call, background vs foreground, transient vs genuine.
 *
 * Fingerprinted by origin + transient/genuine (these DOMExceptions have no stack, so
 * default grouping would merge them straight back into the pre-existing anonymous issue,
 * and would co-mingle genuine DB failures with suspension noise).
 *
 * Side effect: converts what was an anonymous unhandled rejection into a handled,
 * origin-tagged capture. That is intended.
 */
export function captureDbAbort(origin: string, error: unknown): void {
  try {
    const err = unwrapDexieError(error) as { name?: string } | null | undefined;
    const transient = isTransientDbSuspensionError(error);
    Sentry.captureException(error, {
      fingerprint: ['cc-1864-db-abort', origin, transient ? 'transient' : 'genuine'],
      tags: {
        db_abort: true,
        origin,
        db_error_name: err?.name ?? 'unknown',
        transient_suspected: transient,
        in_suspension_window: isWithinSuspensionWindow(),
      },
      contexts: {
        lifecycle: getLifecycleSnapshot(),
      },
    });
  } catch (captureError) {
    // Instrumentation must never become its own crash source (these run inside .catch
    // handlers — a throw here would recreate the very unhandled rejection we capture).
    console.warn('[sentryLogging] captureDbAbort failed', logFriendlyObject(captureError));
  }
  try {
    console.warn(`[db-abort:${origin}]`, logFriendlyObject(error));
  } catch {
    console.warn(`[db-abort:${origin}] (error not serializable)`);
  }
}
